import React, { useState } from 'react'
import { kebabCase } from 'lodash'
import pluralize from 'pluralize'
import { IconTrash, IconAlertTriangle, IconPlus, IconPencil } from '@tabler/icons-react'
import classNames from 'classnames'

import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import { BasicModal } from '@/components/layout/modals/BasicModal'
import { ModalActions } from '@/components/modals/ModalActions'
import { Banner } from '@/components/banners/Banner'
import { Button } from '@/components/buttons/Button'

export const CategoryHeader = ({
  categoryId,
  category,
  readOnly,
  nestedEntriesCount,
  validationError = {},
  isUncategorized = false,
  onDelete,
  onRename,
  onCreateItem,
  deleteMessage,
  showEmptyWarning = true,
  editTestId = 'edit-playbook-name',
  addItemTestId = 'add-entry-item-button',
  accessor,
}) => {
  const [editing, setEditing] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const hasValidationError =
    accessor && validationError.accessor === accessor && validationError?.entry === categoryId

  const handleSubmit = (values, { setSubmitting }) => {
    setSubmitting(false)
    onRename({ name: values.name, categoryId })
    setEditing(false)
  }

  const defaultDeleteMessage = `Are you sure you want to delete the category "${
    category.name
  }"? ${pluralize('entry', nestedEntriesCount, true)} will also be deleted.`

  return (
    <>
      <div
        className="playbook-category"
        id={isUncategorized ? 'uncategorized' : kebabCase(category.name)}
      >
        <div className="playbook-category-draggable">
          {isUncategorized ? (
            <h2 className="playbook-category-name">Uncategorized</h2>
          ) : (
            <>
              {!editing ? (
                <>
                  <h2
                    className={classNames('playbook-category-name', {
                      'status-critical': hasValidationError,
                    })}
                  >
                    {category.name}
                  </h2>
                  {!readOnly && (
                    <Button
                      square
                      small
                      className="hidden-outer"
                      onClick={() => setEditing(true)}
                      data-testid={editTestId}
                    >
                      <IconPencil />
                    </Button>
                  )}
                </>
              ) : (
                <AbstractForm
                  horizontal
                  onSubmit={handleSubmit}
                  existingValues={{ name: category.name }}
                  schema={[{ name: 'name', type: 'text', required: true, autoFocus: true }]}
                  buttonLabel="Update"
                  closeButtonLabel="Cancel"
                  onClose={() => setEditing(false)}
                />
              )}
            </>
          )}
        </div>
        {!editing && !readOnly && (
          <div className="flex flex-align-center medium-gap flex-end">
            {!isUncategorized && (
              <Button
                critical
                square
                small
                data-testid="delete-category-button"
                className="hidden-outer"
                onClick={() => setShowDeleteModal(true)}
              >
                <IconTrash />
              </Button>
            )}

            <Button
              small
              data-testid={addItemTestId}
              onClick={() => onCreateItem(categoryId)}
              style={{ marginRight: '40px' }}
            >
              <IconPlus />
              <span>Add Item</span>
            </Button>
          </div>
        )}
      </div>
      {showEmptyWarning && nestedEntriesCount < 1 && !isUncategorized && (
        <Banner
          header="Warning"
          warning={!hasValidationError}
          critical={hasValidationError}
          icon={<IconAlertTriangle />}
        >
          At least one category entry is required
        </Banner>
      )}

      {showDeleteModal && (
        <BasicModal
          title="Delete Category"
          onClose={() => setShowDeleteModal(false)}
          show={showDeleteModal}
          size="tiny"
        >
          <p>{deleteMessage || defaultDeleteMessage}</p>
          <ModalActions
            onClose={() => setShowDeleteModal(false)}
            onSubmit={() => onDelete(categoryId)}
            isDelete
          />
        </BasicModal>
      )}
    </>
  )
}
